import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdBarChart, MdPlayCircle } from 'react-icons/md';
import AssignmentVector from 'assets/vectors/Tests.svg';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import Loader from 'components/Loader';
import Tabs from 'components/Tabs';

import './index.css';

function Assignments() {
    const [testTypes, setTestTypes] = useState(null);
    const [testTypeIndex, setTestTypeIndex] = useState(0);
    const [assignments, setAssignments] = useState([]);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);

    useEffect(() => {
        axios.get(`questions/assignments/${organization.urlSlug}/`)
            .then(res => {
                setAssignments(res.data.questionSets);
                let testTypeList = [];
                for (let i = 0; i < res.data.questionSets.length; i++) {
                    if (!testTypeList.includes(res.data.questionSets[i].testType)) {
                        testTypeList.push(res.data.questionSets[i].testType);
                    }
                }
                setTestTypes(testTypeList);
            })
            .catch(error => {
                dispatch(setError(error));
            });
    }, []);

    if (testTypes === null) return <Loader />;
    else if (testTypes.length === 0) {
        return (
            <div className='layout'>
                <Helmet>
                    <title>
                        Assignments - {organization.name}
                    </title>
                </Helmet>
                <h4 className='assignments__subHeader'>No assignments.</h4>
            </div>
        );
    }

    const assigned = assignments.filter(assignment => (
        assignment.testType === testTypes[testTypeIndex] &&
        !assignment.startDate
    ));
    const inProgress = assignments.filter(assignment => (
        assignment.testType === testTypes[testTypeIndex] &&
        assignment.startDate && !assignment.completedDate
    ));
    const completed = assignments.filter(assignment => (
        assignment.testType === testTypes[testTypeIndex] &&
        assignment.startDate && assignment.completedDate
    ));

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    Assignments - {organization.name}
                </title>
            </Helmet>
            <div className='assignments'>
                <Tabs
                    className='assignments__tabs'
                    tabs={testTypes}
                    tabIndex={testTypeIndex}
                    onSelect={testTypes.map((_testType, t) => (() => setTestTypeIndex(t)))}
                />
                {inProgress.length > 0 && <h4 className='assignments__subHeader'>In progress</h4>}
                {inProgress.map(assignment => (
                    <button
                        className='assignments__assignment'
                        onClick={() => navigate(`/assignment/${assignment.questionSetId}`)}
                        key={assignment.questionSetId}
                    >
                        <img className='assignments__assignmentVector' src={AssignmentVector} />
                        <div className='assignments__info'>
                            <h2 className='assignments__name'>{assignment.name}</h2>
                            <div className='assignments__progressBar'>
                                <span style={{ width: '50%' }}></span>
                            </div>
                            <div className='assignments__progress'>
                                <p>{assignment.subject}</p>
                            </div>
                        </div>
                        <MdPlayCircle className='assignments__statusIcon' />
                    </button>
                ))}
                {assigned.length > 0 && <h4 className='assignments__subHeader'>Not started</h4>}
                {assigned.map(assignment => (
                    <button
                        className='assignments__assignment'
                        onClick={() => navigate(`/assignment/${assignment.questionSetId}`)}
                        key={assignment.questionSetId}
                    >
                        <img className='assignments__assignmentVector' src={AssignmentVector} />
                        <div className='assignments__info'>
                            <h2 className='assignments__name'>{assignment.name}</h2>
                            <div className='assignments__progressBar'>
                                <span style={{ width: '0%' }}></span>
                            </div>
                            <div className='assignments__progress'>
                                <p>{assignment.subject}</p>
                                <p>assigned on {new Date(assignment.createdAt).toLocaleString('en-us', { year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                            </div>
                        </div>
                        <MdPlayCircle className='assignments__statusIcon' />
                    </button>
                ))}
                {completed.length > 0 && <h4 className='assignments__subHeader'>Completed</h4>}
                {completed.map(assignment => (
                    <button
                        className='assignments__assignment'
                        onClick={() => navigate(`/assignments/report/${assignment.questionSetId}`)}
                        key={assignment.questionSetId}
                    >
                        <img className='assignments__assignmentVector' src={AssignmentVector} />
                        <div className='assignments__info'>
                            <h2 className='assignments__name'>{assignment.name}</h2>
                            <div className='assignments__progressBar'>
                                <span style={{ width: '100%' }}></span>
                            </div>
                            <div className='assignments__progress'>
                                <p>{assignment.subject}</p>
                                <p>{new Date(assignment.completedDate).toLocaleString('en-us', { year: 'numeric', month: 'numeric', day: 'numeric' })}</p>
                            </div>
                        </div>
                        <MdBarChart className='assignments__statusIcon' />
                    </button>
                ))}
            </div>
        </div>
    );
}

export default Assignments;